import { BlobDto } from "../../app/blobs.types";
import {
    AddressDto,
    AuditedAndConcurrencyHandledDto,
    ConcurrencyHandledDto,
    ListItem,
    PagedFilter,
} from "../../app/types";
import { BiometricDeviceUserPrevilage, BiometricType } from "../attendance/attendance.types";
import { BankAccountDetails, PaymentMethod } from "../invoicing/invoicing.types";

export enum Gender {
    Male = 1,
    Female = 2,
}

export interface EmployeesState {
    searchFilter: EmployeeSearchFilter;
}

export interface EmployeeCreateDto extends ConcurrencyHandledDto {
    firstName: string;
    lastName: string;
    startDate: string;
    endDate?: string;
    inactive: boolean;
    employmentContractTypeId: string;
    employmentContractTypeCode?: string;
    employeeNo?: number;
    epfNo?: number;
    email?: string;
    onlineChecking?: boolean;
    designationId?: string;
    departmentId?: string;
    employmentAct?: string;
    branchId?: string;
    teamId?: string;
    projectIds?: string[];
}

export interface Employee extends EmployeeCreateDto, AuditedAndConcurrencyHandledDto {
    id: string;
    isUser?: boolean;
}

export interface EmployeeListItem {
    id: string;
    employeeNo?: number;
    epfNo?: number;
    firstName: string;
    lastName: string;
    startDate: string;
    endDate: string;
    inactive: boolean;
    employmentContractType: string;
    email?: string;
    phone?: string;
    isUser?: boolean;
    department?: string;
    designation?: string;
    branch?: string;
    team?: string;
}

export interface EmployeeSelectListItem {
    id: string;
    employeeNo: number;
    name: string;
    employmentContractType: string;
    department?: string;
    departmentId?: string;
    branch?: string;
    branchId?: string;
    teamId?: string;
    biometricDevicePrevilage?: BiometricDeviceUserPrevilage;
    biometricEnrollments: BiometricType[];
}

export interface EmployeeSelectListOptions {
    includeBiometricTypes: boolean;
}

export interface EmployeePersonalDetailsDto {
    nicNo?: string;
    address?: AddressDto;
    phone?: string;
    birthday?: string;
    fullName?: string;
    nameWithInitials?: string;
    bankAccount?: BankAccountDetails;
    salaryPaymentMethod?: PaymentMethod;
    gender?: Gender;
}

export interface EmployeeSearchFilter extends PagedFilter {
    searchTerm?: string;
    showInactive: boolean;
    departmentId?: string;
    branchId?: string;
    teamId?: string;
    projectId?: string;
    joiningFrom?: string;
    joiningTo?: string;
    resigningFrom?: string;
    resigningTo?: string;
}

export interface EmploymentContractType {
    id: string;
    name: string;
    code: string;
    inactive: string;
    epfEtfPaying: boolean;
    otPaying: boolean;
    includeInPayroll: boolean;
    leaveEligible: boolean;
}

export enum SalaryComponentCategory {
    Allowance = 1,
    Deduction = 2,
}

export interface SalaryComponentTypeDto {
    id: string; // Guid in C# is represented as a string in TypeScript
    category: SalaryComponentCategory;
    name: string;
    description: string;
    inactive: boolean;
    impactBasic: boolean;
    sortOrder: number;
}

export interface SalaryComponentTypeCreateDto {
    category: SalaryComponentCategory;
    name: string;
    description: string;
    inactive: boolean;
    impactBasic: boolean;
    sortOrder: number;
}

export interface SalaryComponentTypeListItemDto {
    id: string; // Guid in C# is represented as a string in TypeScript
    category: SalaryComponentCategory;
    name: string;
    description: string;
    inactive: boolean;
    impactBasic: boolean;
    sortOrder: number;
}

export interface SalaryComponentTypeSelectListItemDto extends ListItem {
    category: SalaryComponentCategory;
}

export interface BasicSalaryListItem extends AuditedAndConcurrencyHandledDto {
    id: string;
    employeeId: string;
    activeFrom: string;
    amount: number;
}

export interface BasicSalaryUpdateDto {
    id: string;
    employeeId: string;
    activeFrom: string;
    amount: number;
    concurrencyKey?: string;
}

export interface SalaryComponentListItem extends AuditedAndConcurrencyHandledDto {
    category: SalaryComponentCategory;
    id: string;
    typeId: string;
    employeeId: string;
    from: string;
    to?: string;
    amount: number;
}

export interface SalaryComponentCreateDto {
    id: string;
    typeId: string;
    employeeId: string;
    from: string;
    to?: string;
    amount: number;
    concurrencyKey?: string;
}

export interface EmployeeWorkSchedule {
    employeeId: string;
    employeeName: string;
    employeeNumber?: number;
    weeklyOffDays: number[];
    weeklyHalfDays: number[];
}

export interface EmployeeWorkScheduleUpdateDto {
    employeeId: string;
    weeklyOffDays: number[];
    weeklyHalfDays: number[];
}

export interface EmployeeDocument extends BlobDto {
    id: string;
}

export interface UpcomingResignationDto {
    id: string;
    name: string;
    employeeNo: number;
    endDate: string;
    department?: string;
    designation?: string;
}

export interface UpcomingResignationFilter {
    asAt: string;
    daysAhead: number;
}
